import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {AsyncPipe, NgClass, NgFor, NgIf} from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import {
  combineLatest,
  forkJoin,
  Observable,
} from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { DIALOG_DATA, DialogRef } from '@shared/dialog/dialog-ref';
import { AppState } from '@root/app-state.module';
import { RootRoutes } from '@root/root-routes';
import { ReportsRoutes } from '@root/reports/components/reports-routes';
import {
  DOWNLOADABLE_REPORT_PREFERENCE,
  DOWNLOADABLE_REPORT_ROUTES,
  DOWNLOADABLE_REPORT_TYPE,
} from '@root/reports/shared/constants/reports.constants';
import { ReportsPermissionsService } from '@root/reports/services/reports-permissions.service';
import { OrderService } from '@root/orders/services/orders.services';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { PreferenceUtils } from '@shared/utils/preference-utils';
import { ActionName } from '@shared/models/effects-names.model';
import {
  DisplayOptions,
  DownloadablePreferenceElement,
  PreferenceCode,
  PreferenceElement,
  UpdatePreference,
} from '@store/user/user.model';
import {
  DownloadReportModalData,
  DownloadStatus,
  ReportsDownload,
  ReportType,
} from '@store/reports/reports.model';
import { selectPrintableReports } from '@store/user/user.selector';
import {
  selectBuildingPermitsReportAvailability,
  selectBuildingSketchReportAvailability,
  selectForeclosureReportAvailability,
  selectReportsDownload,
  selectReportsDownloadStatusByType,
  selectReportTab,
  selectSelectedPropertyInformation,
} from '@store/reports/reports.selector';
import {
  DownloadCustomizedReport,
  GetReportsPaymentStatus,
  StartReportsDownload,
} from '@store/reports/reports.actions';
import { SavePreferenceGroupSilent } from '@store/user/user.actions';
import {
  AddtoCartBuildingPermitsReports,
  AddtoCartBuildingSketch,
  AddtoCartForeclosure,
  GetBuildingPermitsOffer,
  GetBuildingSketchOffer,
  GetForeclosureReportOffer,
} from '@store/orders/orders.actions';
import {
  selectBuildingPermitsOffer,
  selectBuildingSketchOffer,
  selectForeclosureOffer,
} from '@store/orders/orders.selector';
import { CartInfo, CartItem, CartItemState } from '@store/orders/orders.model';
import { PropertyInformation } from '@store/search-board/search-board.model';
import { DirectToAgentService } from '@shared/services/direct-to-agent.service';

@Component({
  selector: 'rlst-download-report-modal-ensemble',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AsyncPipe, NgIf, NgFor, NgClass, ReactiveFormsModule],
  templateUrl: './download-report-modal-ensemble.component.html',
  styleUrls: ['./download-report-modal-ensemble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadReportModalEnsembleComponent implements OnInit {
  readonly data = inject<DownloadReportModalData>(DIALOG_DATA);
  readonly DownloadStatus = DownloadStatus;

  numberOfselectedReports$: Observable<number>;
  selectedReports$: Observable<Array<DownloadablePreferenceElement>>;
  downloadingReports$: Observable<Array<DownloadablePreferenceElement>>;
  isDownloadFinished$: Observable<boolean>;
  isDownloadInProgress = false;
  isLoading = true;
  availableReports: Array<DownloadablePreferenceElement> = [];

  readonly STATUS_KIND: Record<string, string> = {
    [DownloadStatus.Completed]:    'success',
    [DownloadStatus.InProgress]:   'warning',
    [DownloadStatus.Error]:        'error',
    [DownloadStatus.NotAvailable]: 'warning',
  };

  readonly STATUS_TEXT: Record<string, string> = {
    [DownloadStatus.Completed]:    'Completed',
    [DownloadStatus.InProgress]:   'Processing...',
    [DownloadStatus.Error]:        'Error',
    [DownloadStatus.NotAvailable]: 'Not available',
  };

  readonly availableReportsForm: UntypedFormGroup;

  private readonly dialogRef   = inject<DialogRef<string>>(DialogRef);
  private readonly destroyRef  = inject(DestroyRef);
  private readonly fb          = inject(UntypedFormBuilder);
  private readonly router      = inject(Router);
  private readonly store$      = inject<Store<AppState>>(Store);
  readonly reportsPermissionsService = inject(ReportsPermissionsService);
  readonly ordersService             = inject(OrderService);
  private readonly spinnerOverlayService = inject(SpinnerOverlayService);
  private readonly d2aService = inject(DirectToAgentService);

  constructor() {
    this.availableReportsForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.store$.pipe(
      select(selectPrintableReports),
      switchMap((preferenceElements: Array<PreferenceElement>) => {
        const preferenceElements$: Array<Observable<PreferenceElement>> = preferenceElements.map(
          (preferenceElement: PreferenceElement) => {
            const isAvailable$: Observable<boolean> = this.reportsPermissionsService.isReportAvailable(
              DOWNLOADABLE_REPORT_PREFERENCE[preferenceElement.prefCode],
            );
            return isAvailable$.pipe(
              map((isAvailable: boolean) => isAvailable ? preferenceElement : null),
            );
          },
        );
        return combineLatest(preferenceElements$).pipe(
          map((elements: Array<PreferenceElement>) => elements.filter(Boolean)),
        );
      }),
      map((preferenceElements: Array<PreferenceElement>) =>
        preferenceElements.map((preferenceElement: PreferenceElement) => {
          const reportType = DOWNLOADABLE_REPORT_TYPE[preferenceElement.prefCode];
          const downloadStatus$: Observable<DownloadStatus> = this.store$.pipe(
            select(selectReportsDownloadStatusByType(reportType)),
          );
          return { preferenceElement, label: preferenceElement.label, downloadStatus$, reportType };
        }),
      ),
      withLatestFrom(
        this.store$.pipe(select(selectReportTab)),
        this.store$.pipe(select(selectSelectedPropertyInformation)),
        this.store$.pipe(select(selectBuildingPermitsReportAvailability)),
        this.store$.pipe(select(selectBuildingSketchReportAvailability)),
        this.store$.pipe(select(selectForeclosureReportAvailability)),
        this.d2aService.package$
      ),
      switchMap(([
        availableReports,
        tab,
        propertyInformation,
        isBuildingPermitsAvailable,
        isBuildingSketchReportAvailable,
        isForeclosureReportAvailable,
        d2aPackage
      ]: [Array<DownloadablePreferenceElement>, string, PropertyInformation, boolean, boolean, boolean, any]) => {
        const availableReports$ = availableReports.map((report: DownloadablePreferenceElement) =>
          this.ordersService.getProductPurchaseStatus(report.reportType, propertyInformation).pipe(
            map((status: { status: string }) => {
              report.status = status.status;
              if (report.reportType === ReportType.BUILDING_PERMITS)  report.isAvailable = isBuildingPermitsAvailable;
              if (report.reportType === ReportType.BUILDING_SKETCH)   report.isAvailable = isBuildingSketchReportAvailable;
              if (report.reportType === ReportType.FORECLOSURE)       report.isAvailable = isForeclosureReportAvailable;
              return report;
            }),
          ),
        );

        return forkJoin(availableReports$).pipe(
          tap((reports) => {
            reports.forEach((report: DownloadablePreferenceElement) => {
              const control = this.fb.control(
                PreferenceUtils.hasPreferenceValue(report.preferenceElement, DisplayOptions.YES),
              );
              if (tab !== ReportsRoutes.FloodMap && report.reportType === ReportType.FLOODMAP) return;

              if (
                !!d2aPackage && 
                (report.status === 'Not Available' ||
                report.status === CartItemState.NOT_IN_CART ||
                report.status === CartItemState.IN_CART ||
                report.status === CartItemState.VIEW)
              ) {
                return;
              }

              this.availableReports.push(report);
              this.availableReportsForm.addControl(report.reportType, control);

              if (report.reportType === ReportType.BUILDING_PERMITS)
                this.store$.dispatch(new GetBuildingPermitsOffer(this.data.propertyIdentifier.apn));
              if (report.reportType === ReportType.BUILDING_SKETCH)
                this.store$.dispatch(new GetBuildingSketchOffer(this.data.propertyIdentifier.apn));
              if (report.reportType === ReportType.FORECLOSURE)
                this.store$.dispatch(new GetForeclosureReportOffer(this.data.propertyIdentifier.apn));

              if (
                report.status === 'Not Available' ||
                report.status === CartItemState.NOT_IN_CART ||
                report.status === CartItemState.IN_CART ||
                report.status === CartItemState.VIEW
              ) {
                this.availableReportsForm.get(report.reportType)?.disable();
              }
            });
          }),
        );
      }),
      switchMap((preferenceElements: Array<DownloadablePreferenceElement>) => {
        const preferenceElements$ = preferenceElements?.map(
          (preferenceElement: DownloadablePreferenceElement) =>
            preferenceElement.downloadStatus$.pipe(
              map((status: DownloadStatus) =>
                status === DownloadStatus.PaymentRequired ? preferenceElement : null,
              ),
            ),
        );
        return combineLatest(preferenceElements$).pipe(
          map((elements: Array<DownloadablePreferenceElement>) => elements.filter(Boolean)),
        );
      }),
      take(1),
    ).subscribe({
      next: (availableReports: Array<DownloadablePreferenceElement>) => {
        const reports = availableReports
          .map((preferenceElement) => preferenceElement.reportType)
          .filter((reportType: ReportType) => reportType !== ReportType.PROPERTY_PHOTOS);
        if (reports.length) {
          this.store$.dispatch(new GetReportsPaymentStatus(reports));
        }
      },
      complete: () => { this.isLoading = false; },
    });

    this.selectedReports$ = this.availableReportsForm.valueChanges.pipe(
      startWith(this.availableReportsForm.value),
      map((values) =>
        this.availableReports?.filter(
          (reportPreference: DownloadablePreferenceElement) => values[reportPreference.reportType],
        ),
      ),
      shareReplay(1),
    );

    this.downloadingReports$ = this.selectedReports$.pipe(
      map((reports: Array<DownloadablePreferenceElement>) => {
        const reportType = ReportType.CUSTOMIZED;
        const downloadStatus$ = this.store$.pipe(select(selectReportsDownloadStatusByType(reportType)));
        return reports.concat([{ downloadStatus$, reportType, label: 'PDF Generating', preferenceElement: null, status: '' }]);
      }),
    );

    this.numberOfselectedReports$ = this.selectedReports$.pipe(
      map((reports: Array<DownloadablePreferenceElement>) => reports.length),
    );

    this.isDownloadFinished$ = this.downloadingReports$.pipe(
      switchMap((preferenceElements: Array<DownloadablePreferenceElement>) => {
        const downloads$ = preferenceElements.map((el: DownloadablePreferenceElement) =>
          el.downloadStatus$.pipe(
            map((s: DownloadStatus) => s !== DownloadStatus.InProgress && s !== DownloadStatus.NotStarted),
          ),
        );
        return combineLatest(downloads$);
      }),
      map((downloads: Array<boolean>) => downloads.every(Boolean)),
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  navigateToReport(reportType: ReportType): void {
    this.close();
    const route = DOWNLOADABLE_REPORT_ROUTES[reportType];
    if (route) this.router.navigate([RootRoutes.Reports, route]);
  }

  startDownload(): void {
    this.isDownloadInProgress = true;
    this.selectedReports$.pipe(take(1)).subscribe((selectedReports: Array<DownloadablePreferenceElement>) => {
      const reportTypes = selectedReports.map((r: DownloadablePreferenceElement) => r.reportType);
      const formValue = this.availableReportsForm.value;
      const preferenceElementsList = this.availableReports.map((report: DownloadablePreferenceElement) => {
        const displayOption = formValue[report.reportType] ? DisplayOptions.YES : DisplayOptions.NO;
        return PreferenceUtils.setPreferenceValue(report.preferenceElement, displayOption);
      });
      const updatePreference: UpdatePreference = {
        preferenceElementsList,
        prefGroupCode: PreferenceCode.PG_REPORTS_PRINT_SAVE_EMAIL,
      };
      if (reportTypes.length) {
        this.store$.dispatch(new StartReportsDownload({ reportType: reportTypes, outputFlag: this.data.outputFlag }));
        this.store$.dispatch(new DownloadCustomizedReport(reportTypes));
      }
      this.store$.dispatch(new SavePreferenceGroupSilent([updatePreference]));
    });
  }

  download(): void {
    this.store$.pipe(
      select(selectReportsDownload),
      map((reportsDownload: ReportsDownload) => reportsDownload[ReportType.CUSTOMIZED]),
      filter((reportDownload) => reportDownload.status === DownloadStatus.Completed),
      take(1),
    ).subscribe((reportDownload) => {
      this.dialogRef.close(reportDownload.fileLink);
    });
  }

  updateOutputLabel(currentLabel: string): string {
    return this.data.title.includes('Customize Email') && currentLabel.includes('MLS Property Photos')
      ? currentLabel + ' (20 Photos Max)'
      : currentLabel;
  }

  addToCart(report: DownloadablePreferenceElement): void {
    if (report.reportType === ReportType.BUILDING_PERMITS) {
      this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
      this.store$.pipe(select(selectBuildingPermitsOffer), takeUntilDestroyed(this.destroyRef)).subscribe((cart) => {
        this.store$.dispatch(new AddtoCartBuildingPermitsReports(this.getCartItem(cart, 'BPR'), 'customized'));
        this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART);
        this.dialogRef.close();
      });
    } else if (report.reportType === ReportType.BUILDING_SKETCH) {
      this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
      this.store$.pipe(select(selectBuildingSketchOffer), takeUntilDestroyed(this.destroyRef)).subscribe((cart) => {
        this.store$.dispatch(new AddtoCartBuildingSketch(this.getCartItem(cart, 'BSK'), 'customized'));
        this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART);
        this.dialogRef.close();
      });
    } else if (report.reportType === ReportType.FORECLOSURE) {
      this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
      this.store$.pipe(select(selectForeclosureOffer), takeUntilDestroyed(this.destroyRef)).subscribe((cart) => {
        this.store$.dispatch(new AddtoCartForeclosure(this.getCartItem(cart, 'FCL'), 'customized'));
        this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART);
        this.dialogRef.close();
      });
    }
  }

  private getCartItem(cart: CartInfo, productCode: string): CartItem {
    const cartItem = cart.savedItems.find(
      (item) => item.productId === productCode && item.apn === this.data.propertyIdentifier.apn,
    );
    const priceOverrides = cart.priceProduct?.length
      ? cart.priceProduct.filter((item) => item.product_id === productCode)
      : [];
    const cartTotal = priceOverrides.length ? priceOverrides[0].price : cartItem.price;

    return {
      ...cartItem,
      price: cartTotal,
      state: CartItemState.IN_CART,
      requestPayloadPartial: this.data.address,
      requestPayloadFull: JSON.stringify({
        parcelId:          this.data.propertyIdentifier.parcelId,
        fipsCode:          this.data.propertyIdentifier.fipsCode,
        apn:               this.data.propertyIdentifier.apn,
        universalParcelId: this.data.propertyIdentifier.universalParcelId,
      }),
    };
  }
}

